import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie-consent-v1";

/**
 * CookieBanner — dismissible disclosure for analytics cookies.
 * - Shows on first visit (no consent record in localStorage).
 * - One "Got it" button persists consent for ~1 year.
 * - Non-blocking: doesn't gate the site; just transparently discloses.
 * - K-12 friendly tone, plain language, no dark patterns.
 */
export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Defer slightly so it doesn't fight first-paint
        const t = setTimeout(() => setShow(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage blocked — skip banner */
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted_at: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie disclosure"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm bg-white ring-1 ring-gray-200 shadow-2xl rounded-2xl p-4 sm:p-5 z-[100] print:hidden"
      data-testid="cookie-banner"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
        data-testid="cookie-banner-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <span className="bg-yellow-100 text-yellow-700 w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
          <Cookie className="w-4 h-4" />
        </span>
        <div>
          <h3 className="font-bold text-gray-900 text-sm">Cookies &amp; analytics</h3>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            We use a few cookies for login + anonymized analytics. We do
            not track students for ads or sell data.{" "}
            <Link
              to="/privacy"
              className="text-yellow-700 underline font-semibold"
            >
              Learn more
            </Link>
            .
          </p>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-sm py-2.5 rounded-full transition-colors"
        data-testid="cookie-banner-accept"
      >
        Got it
      </button>
    </div>
  );
}
